import { Component, Inject } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AlertView, Matcher, MuteRequest } from '@netfoundry-ui/shared/model';
import { isEmpty, isNil } from 'lodash';
import { alertTypeLabel } from './alert-type-labels';

/**
 * What the dialog needs to describe the alert it was opened from.
 *
 * No alert means the dialog was opened from the page header rather than from a row. It
 * has nothing to narrow to then, so the only mute it can make is the network-wide one
 * and the scope question is not asked at all.
 */
export interface MuteAlertDialogData {
    alert?: AlertView;
}

/**
 * How wide the mute should be, narrowest first.
 *
 * `ALERT` pins every label the alert carries, so the mute covers this one condition and
 * nothing else; `TYPE` drops the matchers and covers the alert type across the whole
 * network, including instances nobody has seen yet; and `NETWORK` drops the alert type as
 * well, so nothing on the network pages anybody. The service reads emptiness as everything
 * in both fields, which is why `NETWORK` sends both empty on purpose.
 */
export type MuteScope = 'ALERT' | 'TYPE' | 'NETWORK';

interface DurationChoice {
    label: string;
    hours?: number;
}

/* Indefinite is last and deliberately not the default. `ends: null` is a mute that
 * outlives whoever set it and the incident that justified it, and the listing is the
 * only thing that will ever remind anyone it exists. */
const DURATIONS: DurationChoice[] = [
    { label: '1 hour', hours: 1 },
    { label: '4 hours', hours: 4 },
    { label: '24 hours', hours: 24 },
    { label: 'Until I revoke it', hours: undefined },
];

/**
 * Creates a mute from one alert.
 *
 * A mute is the only one of the page's three actions that stops a page, so this asks two
 * questions a row button cannot - how wide, and for how long - and invites a reason, which
 * is what the console shows against every alert the mute covers. The scope choice is the one
 * that can do damage: muting by type across the network is legitimate during a known
 * incident and far too wide the rest of the time, and muting the whole network is wider
 * still - so the dialog spells out in words what each choice will cover, and says out loud
 * when the choice stops every page on the network.
 */
@Component({
    selector: 'app-mute-alert-dialog',
    templateUrl: './mute-alert-dialog.component.html',
    styleUrls: ['./mute-alert-dialog.component.scss'],
    standalone: false,
})
export class MuteAlertDialogComponent {
    readonly durations = DURATIONS;
    /** The row the dialog was opened from, or absent when it was opened from the header. */
    readonly alert?: AlertView;

    /* Narrowed by default; the constructor widens it only when it has to. */
    scope: MuteScope = 'ALERT';
    durationIndex = 0;
    reason = '';

    constructor(
        private dialogRef: MatDialogRef<MuteAlertDialogComponent>,
        @Inject(MAT_DIALOG_DATA) data: MuteAlertDialogData
    ) {
        this.alert = data.alert;
        if (this.isNetworkOnly) {
            this.scope = 'NETWORK';
        } else if (!this.canScopeToAlert) {
            /* An alert with no usable labels cannot be narrowed to itself: the alert-only
             * scope would send no matchers, and no matchers means the whole network. Start
             * on the honest option rather than letting the default lie. */
            this.scope = 'TYPE';
        }
    }

    /**
     * True when there is no alert to narrow to, so the mute can only be the network-wide
     * one. The scope question is hidden rather than shown with two of its three options
     * disabled: there is nothing to choose.
     */
    get isNetworkOnly(): boolean {
        return isNil(this.alert);
    }

    /** The labels that would be pinned, in the order the detector emitted them. */
    get matchers(): Matcher[] {
        return Object.entries(this.alert?.labels || {})
            .filter(([label, value]) => !isEmpty(label) && !isEmpty(value))
            .map(([label, value]) => ({ label, op: 'EQ' as const, value }));
    }

    /**
     * An alert with no usable labels cannot be narrowed to itself — with no matchers the
     * request would mute the alert type across the network while the reader believed
     * they had muted one condition. The scope is forced to the honest option instead.
     */
    get canScopeToAlert(): boolean {
        return !this.isNetworkOnly && this.matchers.length > 0;
    }

    get isIndefinite(): boolean {
        return this.durations[this.durationIndex]?.hours === undefined;
    }

    /** True when the choice silences the network outright, which earns its own warning. */
    get isNetworkWide(): boolean {
        return this.scope === 'NETWORK';
    }

    /**
     * Plain words for what is about to be silenced.
     *
     * The readable name, the same one the table and the drawer show, so the sentence
     * describes the row the reader clicked. The mute itself still names the raw type —
     * see {@link confirm}.
     */
    get scopeSummary(): string {
        const type = this.typeLabel;
        if (this.scope === 'NETWORK') {
            return 'Every alert in this network, of every type, including ones that have not happened yet.';
        }
        if (this.scope === 'TYPE') {
            return `Every ${type} alert in this network, including ones that have not happened yet.`;
        }
        const pinned = this.matchers.map((matcher) => `${matcher.label}=${matcher.value}`).join(', ');
        return `Only ${type} alerts matching ${pinned}.`;
    }

    /** The readable alert type, for the scope option that names it. */
    get typeLabel(): string {
        return this.alert ? alertTypeLabel(this.alert.alertType) : '';
    }

    setScope(scope: MuteScope): void {
        if (this.isNetworkOnly || (scope === 'ALERT' && !this.canScopeToAlert)) {
            return;
        }
        this.scope = scope;
    }

    setDuration(index: number): void {
        this.durationIndex = index;
    }

    cancel(): void {
        this.dialogRef.close();
    }

    /**
     * Closes with the request to send.
     *
     * There is no `starts`, because a mute begins when the service stamps it and that stamp
     * is what the window rule is checked against. `ends` is null only for the explicit
     * indefinite choice, and a blank reason is left off the request rather than sent as an
     * empty string.
     */
    confirm(): void {
        const hours = this.durations[this.durationIndex]?.hours;
        const reason = this.reason.trim();
        /* Empty is how the contract spells "everything", in both fields. The whole-type
         * scope empties the matchers; the network scope empties the alert types too. */
        const request: MuteRequest = {
            matchers: this.scope === 'ALERT' ? this.matchers : [],
            alertTypes: this.scope === 'NETWORK' || !this.alert ? [] : [this.alert.alertType],
            ends: hours === undefined ? null : new Date(Date.now() + hours * 60 * 60 * 1000).toISOString(),
            reason: isEmpty(reason) ? undefined : reason,
        };
        this.dialogRef.close(request);
    }
}
